import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Headphones, Clock, User, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLiveSupport } from '../contexts/LiveSupportContext';

interface LiveSupportChatProps {
  onClose: () => void;
}

const LiveSupportChat: React.FC<LiveSupportChatProps> = ({ onClose }) => {
  const { currentUser } = useAuth();
  const { activeTicket, messages, sendSupportMessage, closeTicket } = useLiveSupport();
  const [messageText, setMessageText] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    await sendSupportMessage(messageText);
    setMessageText('');
  };

  const handleCloseTicket = async () => {
    setIsClosing(true);
    try {
      await closeTicket(true);
      onClose();
    } catch (error) {
      console.error('Ticket kapatılamadı:', error);
    } finally {
      setIsClosing(false);
      setShowCloseConfirm(false);
    }
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = () => {
    if (!activeTicket) return '';
    switch (activeTicket.status) {
      case 'waiting':
        return 'Destek temsilcisi bekleniyor...';
      case 'active':
        return `${activeTicket.assignedAdminName || 'Destek'} ile görüşüyorsunuz`;
      default:
        return '';
    }
  };

  const getStatusColor = () => {
    if (!activeTicket) return 'bg-gray-500';
    switch (activeTicket.status) {
      case 'waiting':
        return 'bg-yellow-500';
      case 'active':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (!activeTicket) return null;

  return (
    <div className="flex flex-col h-full relative">
      {/* Close Confirmation Modal */}
      <AnimatePresence>
        {showCloseConfirm && (
          <>
            {/* Backdrop with blur */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-dark-900/80 backdrop-blur-sm z-10 rounded-2xl"
            />

            {/* Confirmation Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="absolute inset-0 flex items-center justify-center z-20 p-4"
            >
              <div className="bg-dark-800 border border-dark-700 rounded-xl p-6 max-w-sm w-full shadow-2xl">
                <h3 className="text-lg font-bold text-white mb-2">
                  Görüşmeyi Sonlandır
                </h3>
                <p className="text-gray-400 text-sm mb-6">
                  Görüşmeyi sonlandırmak istediğinizden emin misiniz? Bu işlem geri alınamaz.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowCloseConfirm(false)}
                    className="flex-1 py-2 px-4 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleCloseTicket}
                    disabled={isClosing}
                    className="flex-1 py-2 px-4 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg transition-colors"
                  >
                    {isClosing ? 'Kapatılıyor...' : 'Sonlandır'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-dark-700/50">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
            <Headphones className="w-4 h-4 text-white" />
          </div>
          <div>
            <span className="text-sm font-medium text-white">Canlı Destek</span>
            <div className="flex items-center space-x-1 text-xs text-gray-400">
              <div className={`w-2 h-2 rounded-full ${getStatusColor()} animate-pulse`} />
              <span>{getStatusText()}</span>
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowCloseConfirm(true)}
          className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
          title="Görüşmeyi Sonlandır"
        >
          <X className="w-4 h-4 text-gray-400 hover:text-red-400" />
        </button>
      </div>

      {/* Ticket Info */}
      <div className="px-4 py-2 bg-dark-700/30 border-b border-dark-700/50">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">Konu: <span className="text-white">{activeTicket.subject}</span></span>
          <span className="text-gray-500 flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>{formatTime(activeTicket.createdAt)}</span>
          </span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* System Message */}
        <div className="text-center">
          <span className="text-xs text-gray-500 bg-dark-700/30 px-3 py-1 rounded-full">
            Destek talebi oluşturuldu
          </span>
        </div>

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.senderId === currentUser?.uid ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] px-3 py-2 rounded-lg ${
                msg.senderId === currentUser?.uid
                  ? 'bg-green-500 text-white'
                  : 'bg-dark-700/50 text-gray-100'
              }`}
            >
              <div className={`text-xs mb-1 flex items-center space-x-1 ${
                msg.senderId === currentUser?.uid ? 'text-green-100' : 'text-gray-400'
              }`}>
                {msg.senderRole === 'admin' ? (
                  <>
                    <Shield className="w-3 h-3" />
                    <span>{msg.senderName}</span>
                  </>
                ) : (
                  <>
                    <User className="w-3 h-3" />
                    <span>{msg.senderName}</span>
                  </>
                )}
              </div>
              <div className="text-sm">{msg.text}</div>
              <div className="text-xs opacity-70 mt-1">
                {formatTime(msg.timestamp)}
              </div>
            </div>
          </div>
        ))}

        {/* Waiting indicator */}
        {activeTicket.status === 'waiting' && (
          <div className="text-center py-4">
            <div className="inline-flex items-center space-x-2 text-yellow-400 text-sm">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span>Destek temsilcisi bekleniyor</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-dark-700/50">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Mesajınızı yazın..."
            className="flex-1 px-3 py-2 bg-dark-700/50 border border-dark-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
          />
          <button
            type="submit"
            disabled={!messageText.trim()}
            className="p-2 bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default LiveSupportChat;
